import { UUID } from '@cmg/common';
import { Button, SnackbarManager } from '@cmg/design-system';
import React, { useCallback, useMemo } from 'react';

import { useMatchingCmgEntityKeys } from '../../../../../../../common/hooks/useMatchingCmgEntityKeys';
import { useGetPublishedOfferingEntitlementsAndCheckInstitutionalIndicationAccess } from '../../../../../../../common/util/check-access/useGetPublishedOfferingEntitlementsAndCheckInstitutionalIndicationAccess';
import {
  AllocationAcknowledgement,
  AllocationAcknowledgementNewStatus,
} from '../../../../../../../graphql';
import { updateDemandGridRowsReactiveVar } from '../../../../../../order-book/institutional-demand/hooks/useUpdateDemandGridRowsReactiveVar';
import { canAcknowledgeAllocation } from '../../../../../../order-book/utils';
import { useOfferingSidePanel_UnackknowledgeInstutionalFinalAllocationMutation } from '../../../../../graphql';
import { useIndicationActivitySyndicateContext } from '../../../context/IndicationActivitySyndicateContext';
import { useIndicationActivity_UpdateFinalAllocationAcknowledgementStatusMutation } from '../graphql/__generated__';

export type Props = Readonly<{
  indicationId?: string;
  allocationVersion?: string;
  offeringId?: UUID;
  investorReplyStatus: AllocationAcknowledgement;
  billingAndDeliveryAgentCmgEntityKey: string | null;
}>;

const useSyndicateAllocation = ({
  offeringId,
  indicationId,
  allocationVersion,
  investorReplyStatus,
  billingAndDeliveryAgentCmgEntityKey,
}: Props) => {
  const {
    access: { canBillingAndDeliveryAgentUpdateAllocation },
  } = useIndicationActivitySyndicateContext();

  const matchingCmgEntityKeys = useMatchingCmgEntityKeys();

  const { canUpdate: canUpdateInstitutionalIndication } =
    useGetPublishedOfferingEntitlementsAndCheckInstitutionalIndicationAccess({
      offeringId,
    });

  const canAcknowledge = canAcknowledgeAllocation({
    canBndAgentUpdateInstitutionalIndication: canBillingAndDeliveryAgentUpdateAllocation,
    canUserUpdateInstitutionalIndication: canUpdateInstitutionalIndication,
    matchingCmgEntityKeys,
    bndAgentCmgEntityKey: billingAndDeliveryAgentCmgEntityKey,
  });

  const [updateAcknowledgementStatus, { loading: updateStatusLoading, error: updateStatusError }] =
    useIndicationActivity_UpdateFinalAllocationAcknowledgementStatusMutation();

  const [
    unacknowledgeAllocation,
    { loading: unacknowledgeAllocationLoading, error: unacknowledgeAllocationError },
  ] = useOfferingSidePanel_UnackknowledgeInstutionalFinalAllocationMutation();

  const handleAcknowledge = useCallback(
    async (status: AllocationAcknowledgementNewStatus) => {
      try {
        if (!offeringId || !indicationId || !allocationVersion) {
          throw new Error('Missing offeringId, indicationId or allocationVersion');
        }

        // can update allocation && indication
        await updateAcknowledgementStatus({
          variables: {
            offeringId: offeringId,
            indicationId,
            versionId: allocationVersion,
            investorReplyStatus: status,
          },
        });

        SnackbarManager.success('Successfully updated allocation acknowledgement status');

        updateDemandGridRowsReactiveVar({ fetchIds: [indicationId] });
      } catch {
        SnackbarManager.error('Failed to update allocation acknowledgement status');
      }
    },
    [allocationVersion, indicationId, offeringId, updateAcknowledgementStatus]
  );

  const handleUnackowledge = useCallback(async () => {
    try {
      if (!offeringId || !indicationId || !allocationVersion) {
        throw new Error('Missing offeringId, indicationId or allocationVersion');
      }

      await unacknowledgeAllocation({
        variables: {
          offeringId: offeringId,
          indicationId,
          versionId: allocationVersion,
        },
      });

      SnackbarManager.success('Successfully unacknowledged allocation');

      updateDemandGridRowsReactiveVar({ fetchIds: [indicationId] });
    } catch {
      SnackbarManager.error('Failed to unacknowledge allocation');
    }
  }, [allocationVersion, indicationId, offeringId, unacknowledgeAllocation]);

  const errors = useMemo(
    () =>
      [updateStatusError, unacknowledgeAllocationError]
        .map(error => error?.message)
        .filter((val): val is string => Boolean(val)),
    [updateStatusError, unacknowledgeAllocationError]
  );

  const isDeclineVisible =
    canAcknowledge &&
    (investorReplyStatus === AllocationAcknowledgement.Unacknowledged ||
      investorReplyStatus === AllocationAcknowledgement.Acknowledged);
  const isAcknowledgementVisible =
    canAcknowledge && investorReplyStatus === AllocationAcknowledgement.Unacknowledged;
  const isUndoVisible =
    canAcknowledge &&
    (investorReplyStatus === AllocationAcknowledgement.Rejected ||
      investorReplyStatus === AllocationAcknowledgement.Acknowledged);

  const actionButtons = useMemo(
    () => [
      ...(isDeclineVisible
        ? [
            <Button
              key="decline"
              variant="outlined"
              onClick={() => handleAcknowledge(AllocationAcknowledgementNewStatus.Rejected)}
              disabled={updateStatusLoading}
            >
              Decline
            </Button>,
          ]
        : []),
      ...(isAcknowledgementVisible
        ? [
            <Button
              key="acknowledge"
              variant="contained"
              onClick={() => handleAcknowledge(AllocationAcknowledgementNewStatus.Acknowledged)}
              disabled={updateStatusLoading}
            >
              Acknowledge
            </Button>,
          ]
        : []),
      ...(isUndoVisible
        ? [
            <Button
              key="undo"
              variant="outlined"
              onClick={handleUnackowledge}
              disabled={unacknowledgeAllocationLoading}
            >
              Undo
            </Button>,
          ]
        : []),
    ],
    [
      handleAcknowledge,
      handleUnackowledge,
      isAcknowledgementVisible,
      isDeclineVisible,
      isUndoVisible,
      unacknowledgeAllocationLoading,
      updateStatusLoading,
    ]
  );

  return {
    actionButtons,
    errors,
  };
};

export default useSyndicateAllocation;
